import { useContext, useEffect, useRef } from "react";
import mapboxgl from "mapbox-gl";

import { MapContext } from "fond/map/MapProvider";
import { useStyleEpoch } from "fond/map/useStyleEpoch";

import { useImageOverlay } from "./useImageOverlay";

const SOURCE_ID = "image-overlay-source";
const LAYER_ID = "image-overlay-layer";

/**
 * Manages the Mapbox image source and raster layer for displaying
 * the image overlay on the map.
 */
const MapImageLayer: React.FC = () => {
  const { map } = useContext(MapContext);
  const { imageUrl, imageCorners, opacity, isVisible } = useImageOverlay();
  const addedRef = useRef(false);
  const styleEpoch = useStyleEpoch(map);

  // Add or update the image source and layer
  useEffect(() => {
    if (!map || !imageUrl || !imageCorners) {
      // Remove if corners are no longer available
      if (map && addedRef.current) {
        if (map.getLayer(LAYER_ID)) map.removeLayer(LAYER_ID);
        if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
        addedRef.current = false;
      }
      return;
    }

    const source = map.getSource(SOURCE_ID) as mapboxgl.ImageSource | undefined;
    if (source) {
      source.updateImage({ url: imageUrl, coordinates: imageCorners });
    } else {
      map.addSource(SOURCE_ID, {
        type: "image",
        url: imageUrl,
        coordinates: imageCorners,
      });
      map.addLayer({
        id: LAYER_ID,
        type: "raster",
        source: SOURCE_ID,
      });
      addedRef.current = true;
    }
  }, [map, imageUrl, imageCorners, styleEpoch]);

  // Update opacity
  useEffect(() => {
    if (map?.getLayer(LAYER_ID)) {
      map.setPaintProperty(LAYER_ID, "raster-opacity", opacity);
    }
  }, [map, opacity, imageUrl, imageCorners, styleEpoch]);

  // Update visibility
  useEffect(() => {
    if (map?.getLayer(LAYER_ID)) {
      map.setLayoutProperty(LAYER_ID, "visibility", isVisible ? "visible" : "none");
    }
  }, [map, isVisible, styleEpoch]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (!map) return;
      try {
        if (map.getLayer(LAYER_ID)) map.removeLayer(LAYER_ID);
        if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
      } catch {
        // Map may already be destroyed when the modal closes
      }
      addedRef.current = false;
    };
  }, [map]);

  return null;
};

export default MapImageLayer;
